import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../utils/ThemeContext';

/**
 * Premium Floating Dark Mode Toggle Component
 * Positioned just above the bottom navigation with glass morphism effect
 */
export const DarkModeToggle: React.FC = () => {
  const { theme, toggleTheme } = useTheme();

  return (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 0.3, type: 'spring', stiffness: 300, damping: 25 }}
      className="fixed z-40 pointer-events-none"
      style={{
        bottom: 'calc(var(--bottom-nav-height) + max(env(safe-area-inset-bottom), 16px) + 16px)',
        right: '20px'
      }}
    >
      <motion.button
        onClick={toggleTheme}
        whileHover={{ scale: 1.1, y: -2 }}
        whileTap={{ scale: 0.95 }}
        className={`
          pointer-events-auto relative w-14 h-14 rounded-full shadow-xl hover:shadow-2xl
          transition-all duration-300 flex items-center justify-center
          backdrop-blur-xl border-2
          ${
            theme === 'dark'
              ? 'bg-neutral-800/90 dark:bg-neutral-800/80 border-neutral-600/50 text-amber-400'
              : 'bg-white/90 border-neutral-300/50 text-slate-800'
          }
        `}
        aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
        title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
      >
        {/* Animated Icon */}
        <motion.div
          key={theme}
          initial={{ scale: 0, rotate: -180, opacity: 0 }}
          animate={{ scale: 1, rotate: 0, opacity: 1 }}
          exit={{ scale: 0, rotate: 180, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
          className="flex items-center justify-center text-2xl"
        >
          {theme === 'light' ? '🌙' : '☀️'}
        </motion.div>

        {/* Glow Effect */}
        <motion.div
          className={`absolute inset-0 rounded-full ${
            theme === 'dark' 
              ? 'bg-amber-400/20' 
              : 'bg-slate-400/20'
          }`}
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0, 0.3]
          }}
          transition={{ 
            repeat: Infinity, 
            duration: 3,
            ease: 'easeInOut'
          }}
        />
      </motion.button>
    </motion.div>
  );
};
